import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Layout } from "@/components/Layout";
import { CookieBanner } from "@/components/CookieBanner";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import HumanProfile from "@/pages/HumanProfile";
import Pets from "@/pages/Pets";
import Workspaces from "@/pages/Workspaces";
import CheckIns from "@/pages/CheckIns";
import WellnessLogs from "@/pages/WellnessLogs";
import Rituals from "@/pages/Rituals";
import BondScore from "@/pages/BondScore";
import Intelligence from "@/pages/Intelligence";
import AICoach from "@/pages/AICoach";
import Reports from "@/pages/Reports";
import CareCircle from "@/pages/CareCircle";
import VoiceSupport from "@/pages/VoiceSupport";
import AgentCenter from "@/pages/AgentCenter";
import Privacy from "@/pages/Privacy";
import Billing from "@/pages/Billing";
import Support from "@/pages/Support";
import Admin from "@/pages/Admin";
import Legal from "@/pages/Legal";
import Devices from "@/pages/Devices";
import Download from "@/pages/Download";

// Pages reachable without an active subscription (checkout itself, help, and account privacy)
const SUBSCRIPTION_EXEMPT_PATHS = ["/billing", "/support", "/privacy"];

const Private = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="app-loading">Loading Soula Paws...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  // Paid-only platform: users without an active/trialing subscription (and no admin comp)
  // must complete Stripe checkout (card + free trial) before using the app.
  if (user.needsSubscription && !SUBSCRIPTION_EXEMPT_PATHS.includes(location.pathname)) {
    return <Navigate to="/billing?startTrial=1" replace />;
  }
  return <Layout>{children}</Layout>;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<PublicOnly><AuthPage mode="login" /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><AuthPage mode="register" /></PublicOnly>} />
          <Route path="/forgot-password" element={<PublicOnly><AuthPage mode="forgot" /></PublicOnly>} />
          <Route path="/reset-password/:token" element={<PublicOnly><AuthPage mode="reset" /></PublicOnly>} />
          <Route path="/dashboard" element={<Private><Dashboard /></Private>} />
          <Route path="/profile" element={<Private><HumanProfile /></Private>} />
          <Route path="/pets" element={<Private><Pets /></Private>} />
          <Route path="/workspaces" element={<Private><Workspaces /></Private>} />
          <Route path="/check-ins" element={<Private><CheckIns /></Private>} />
          <Route path="/wellness" element={<Private><WellnessLogs /></Private>} />
          <Route path="/rituals" element={<Private><Rituals /></Private>} />
          <Route path="/devices" element={<Private><Devices /></Private>} />
          <Route path="/bond-score" element={<Private><BondScore /></Private>} />
          <Route path="/intelligence" element={<Private><Intelligence /></Private>} />
          <Route path="/coach" element={<Private><AICoach /></Private>} />
          <Route path="/reports" element={<Private><Reports /></Private>} />
          <Route path="/care-circle" element={<Private><CareCircle /></Private>} />
          <Route path="/voice-support" element={<Private><VoiceSupport /></Private>} />
          <Route path="/agents" element={<Private><AgentCenter /></Private>} />
          <Route path="/privacy" element={<Private><Privacy /></Private>} />
          <Route path="/billing" element={<Private><Billing /></Private>} />
          <Route path="/support" element={<Private><Support /></Private>} />
          <Route path="/admin" element={<Private adminOnly><Admin /></Private>} />
          <Route path="/legal/:doc" element={<Legal />} />
          <Route path="/download" element={<Download />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
        <CookieBanner />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
